import React from 'react';
import { 
  Building2, 
  MapPin, 
  Landmark, 
  FileText, 
  Settings as SettingsIcon,
  Edit3,
  Mail,
  Phone,
  CreditCard,
  Image as ImageIcon,
  Award,
  Globe
} from 'lucide-react';

/**
 * Modern Company Details Cards - Replaces old tile design
 */

export const ModernInfoCard = ({ title, icon: Icon, onEdit, children, gradient = 'from-blue-500 to-blue-600' }) => {
  return (
    <div className="group relative bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100">
      {/* Gradient Header */}
      <div className={`bg-gradient-to-r ${gradient} p-4`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white bg-opacity-20 rounded-lg backdrop-blur-sm">
              <Icon className="w-5 h-5 text-white" />
            </div>
            <h3 className="text-lg font-bold text-white">{title}</h3>
          </div>
          {onEdit && (
            <button
              onClick={onEdit}
              className="flex items-center gap-2 px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 backdrop-blur-sm text-white rounded-lg font-medium transition-all duration-200 hover:scale-105"
            >
              <Edit3 className="w-4 h-4" />
              <span className="hidden sm:inline">Edit</span>
            </button>
          )}
        </div>
      </div>

      {/* Card Content */}
      <div className="p-6">
        {children}
      </div>
    </div>
  );
};

export const InfoRow = ({ icon: Icon, label, value, highlight = false }) => {
  return (
    <div className="flex items-start gap-3 py-2 border-b border-gray-100 last:border-0">
      {Icon && (
        <div className="flex-shrink-0 mt-0.5">
          <Icon className="w-4 h-4 text-gray-400" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">{label}</div>
        <div className={`text-sm ${highlight ? 'font-semibold text-gray-900' : 'text-gray-700'} break-words`}>
          {value || <span className="text-gray-400">Not set</span>}
        </div>
      </div>
    </div>
  );
};

export const ImagePreview = ({ src, alt, label }) => {
  return (
    <div className="flex flex-col items-center gap-2 p-4 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200 hover:border-blue-300 transition-colors">
      {src ? (
        <>
          <img 
            src={src} 
            alt={alt}
            className="w-24 h-24 object-contain rounded-lg bg-white border border-gray-200 shadow-sm"
          />
          <span className="text-xs font-medium text-gray-600">{label}</span>
          <span className="text-xs text-green-600 font-medium">✓ Uploaded</span>
        </>
      ) : (
        <>
          <div className="w-24 h-24 flex items-center justify-center bg-gray-100 rounded-lg border border-gray-200">
            <ImageIcon className="w-8 h-8 text-gray-300" />
          </div>
          <span className="text-xs font-medium text-gray-400">{label}</span>
          <span className="text-xs text-gray-400">Not uploaded</span>
        </>
      )}
    </div>
  );
};

export const QuickActionButton = ({ icon: Icon, label, onClick, color = 'blue' }) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-700 hover:bg-blue-100 border-blue-200',
    purple: 'bg-purple-50 text-purple-700 hover:bg-purple-100 border-purple-200',
    green: 'bg-green-50 text-green-700 hover:bg-green-100 border-green-200',
    orange: 'bg-orange-50 text-orange-700 hover:bg-orange-100 border-orange-200'
  };

  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-3 p-4 rounded-xl border-2 font-medium transition-all duration-200 hover:scale-105 hover:shadow-md ${colorClasses[color]}`}
    >
      <Icon className="w-5 h-5" />
      <span>{label}</span>
    </button>
  );
};

export const StatCard = ({ icon: Icon, label, value, color = 'blue' }) => {
  const colorClasses = {
    blue: 'from-blue-500 to-blue-600',
    purple: 'from-purple-500 to-purple-600',
    green: 'from-green-500 to-green-600',
    orange: 'from-orange-500 to-orange-600',
    red: 'from-red-500 to-red-600'
  };

  return (
    <div className={`bg-gradient-to-br ${colorClasses[color]} rounded-2xl p-6 text-white shadow-lg hover:shadow-xl transition-shadow duration-300`}>
      <div className="flex items-center justify-between mb-4">
        <div className="p-3 bg-white bg-opacity-20 rounded-xl backdrop-blur-sm">
          <Icon className="w-6 h-6" />
        </div>
      </div>
      <div className="text-3xl font-bold mb-1">{value}</div>
      <div className="text-sm opacity-90">{label}</div>
    </div>
  );
};
































